import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, Trash2, HelpCircle, Key } from 'lucide-react'
import { APIEndpoint } from '../../stores/apiStore'
import EmptyStateIllustration from '../ui/EmptyStateIllustration'

interface QueryParamsEditorProps {
  params: Record<string, string>
  onParamsChange: (params: Record<string, string>) => void
  onAddParam: (key: string, value: string) => void
  onRemoveParam: (key: string) => void
  endpoint: APIEndpoint | null
}

const QueryParamsEditor: React.FC<QueryParamsEditorProps> = ({
  params,
  onParamsChange,
  onAddParam,
  onRemoveParam,
  endpoint
}) => {
  const [newParamKey, setNewParamKey] = useState('')
  const [newParamValue, setNewParamValue] = useState('')

  const handleParamChange = (key: string, value: string) => {
    const updatedParams = { ...params, [key]: value }
    onParamsChange(updatedParams)
  }

  const handleAddParam = () => {
    if (newParamKey.trim() && newParamValue.trim()) {
      onAddParam(newParamKey.trim(), newParamValue.trim())
      setNewParamKey('')
      setNewParamValue('')
    }
  }

  const handleAddRequiredParam = (paramName: string) => {
    const param = endpoint?.queryParameters?.find(p => p.name === paramName)
    if (param && param.example) {
      onAddParam(paramName, param.example.toString())
    }
  }

  const handleUseDemoApiKey = () => {
    onAddParam('api_key', '582c2b53-56d5-49a2-a239-7c36e5b0c789')
  }

  const requiredParams = endpoint?.queryParameters?.filter(p => p.required) || []
  const optionalParams = endpoint?.queryParameters?.filter(p => !p.required) || []

  return (
    <div className="p-4 space-y-6">
      {!endpoint ? (
        <EmptyStateIllustration
          type="query-params"
          title="No Endpoint Selected"
          description="Please select an endpoint from the sidebar to configure query parameters and add test credentials."
        />
      ) : (
        <>
      {/* Required Parameters */}
      {requiredParams.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Required Parameters
            </h3>
            {/* Demo API Key Button */}
            {requiredParams.some(param => param.name === 'api_key') && (
              <button
                onClick={handleUseDemoApiKey}
                className="flex items-center space-x-2 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-sm"
              >
                <Key className="w-3 h-3" />
                <span>Use Demo API Key</span>
              </button>
            )}
          </div>
          <div className="space-y-2">
            {requiredParams.map((param) => (
              <div key={param.name} className="space-y-2">
                <div className="flex items-center space-x-2">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    {param.name}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  {param.description && (
                    <div 
                      className="relative group cursor-help"
                      title={param.description}
                    >
                      <HelpCircle className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                      <div className="absolute left-0 top-5 z-50 w-64 p-2 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        {param.description}
                        <div className="absolute -top-1 left-2 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45"></div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={param.name}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-white text-sm"
                    />
                  </div>
                  <div className="flex-1">
                    <input
                      type={param.name.toLowerCase().includes('key') || param.name.toLowerCase().includes('token') || param.name.toLowerCase().includes('password') ? 'password' : 'text'}
                      value={params[param.name] || ''}
                      onChange={(e) => handleParamChange(param.name, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      placeholder={param.example?.toString() || ''}
                    />
                  </div>
                  {!params[param.name] && (
                    <button
                      onClick={() => handleAddRequiredParam(param.name)}
                      className="px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                    >
                      Use Example
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Existing Parameters */}
      {Object.keys(params).filter(key => {
        // Filter out parameters that are already shown in Required Parameters section
        const requiredParamNames = endpoint?.queryParameters?.filter(p => p.required).map(p => p.name) || []
        return !requiredParamNames.includes(key)
      }).length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Current Parameters
          </h3>
          <div className="space-y-2">
            <AnimatePresence>
              {Object.entries(params).filter(([key]) => {
                // Filter out parameters that are already shown in Required Parameters section
                const requiredParamNames = endpoint?.queryParameters?.filter(p => p.required).map(p => p.name) || []
                return !requiredParamNames.includes(key)
              }).map(([key, value]) => (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="flex items-center space-x-2"
                >
                  <input
                    type="text"
                    value={key}
                    onChange={(e) => {
                      const newParams = { ...params }
                      delete newParams[key]
                      newParams[e.target.value] = value
                      onParamsChange(newParams)
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    placeholder="Parameter name"
                  />
                  <input
                    type={key.toLowerCase().includes('key') || key.toLowerCase().includes('token') || key.toLowerCase().includes('password') ? 'password' : 'text'}
                    value={value}
                    onChange={(e) => handleParamChange(key, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    placeholder="Parameter value"
                  />
                  <button
                    onClick={() => onRemoveParam(key)}
                    className="p-2 text-red-500 hover:text-red-700 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Add New Parameter */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Add Parameter
        </h3>
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={newParamKey}
            onChange={(e) => setNewParamKey(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            placeholder="Parameter name"
          />
          <input
            type={newParamKey.toLowerCase().includes('key') || newParamKey.toLowerCase().includes('token') || newParamKey.toLowerCase().includes('password') ? 'password' : 'text'}
            value={newParamValue}
            onChange={(e) => setNewParamValue(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            placeholder="Parameter value"
          />
          <button
            onClick={handleAddParam}
            className="p-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Optional Parameters Helper */}
      {optionalParams.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Optional Parameters
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {optionalParams.map((param) => (
              <button
                key={param.name}
                onClick={() => handleAddRequiredParam(param.name)}
                className="p-2 text-left border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {param.name}
                  </span>
                  {param.description && (
                    <div 
                      className="relative group cursor-help"
                      title={param.description}
                    >
                      <HelpCircle className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                      <div className="absolute left-0 top-5 z-50 w-64 p-2 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        {param.description}
                        <div className="absolute -top-1 left-2 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45"></div>
                      </div>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
        </>
      )}
    </div>
  )
}

export default QueryParamsEditor
